// Sign-In Link Check page — shown after user submits email on /login.
// No OTP / 6-digit code input. User clicks link in their inbox (same browser).
// "Refresh" button just reloads this page — if the landing route has completed
// auth (tokens in localStorage), the top-level auth check sends them to /.
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { Row, Col, notification } from 'antd';
import { MailOutlined, ReloadOutlined } from '@ant-design/icons';
import Lottie from 'lottie-react';
import { ThemeButton } from '../Styles/Theme';
import { LoginWrapper } from './LoginStyle';
import useLottieData from '../../hooks/useLottieData';
import { resendMagicLinkApi } from '../../store/actions/loginAction';
import { isLogin } from '../../utils/helpers';

const SignInLinkCheck = (props) => {
	const { resend } = props;
	const navigate = useNavigate();
	const [resending, setResending] = useState(false);
	const [cooldown, setCooldown] = useState(0);
	const [mfaSessionToken, setMfaSessionToken] = useState(null);
	const [maskedEmail, setMaskedEmail] = useState('');
	// Fall back to static PNG if the Lottie animation fails to load.
	const loginLottie = useLottieData('/lottie/login.json');
	const [lottieFailed, setLottieFailed] = useState(false);
	const cooldownTimerRef = useRef(null);

	// If user is already logged in (e.g. they clicked the link in same browser,
	// tokens landed in localStorage, then they refocused/reloaded this tab) → go to /.
	useEffect(() => {
		if (isLogin()) {
			navigate('/', { replace: true });
			return;
		}
		const token = localStorage.getItem('mfaSessionToken');
		if (!token) {
			notification.warning({ message: 'Please sign in again.' });
			navigate('/login', { replace: true });
			return;
		}
		setMfaSessionToken(token);
		setMaskedEmail(localStorage.getItem('mfaMaskedEmail') || '');
	}, [navigate]);

	useEffect(() => {
		return () => {
			if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
		};
	}, []);

	const startCooldown = (seconds) => {
		setCooldown(seconds);
		if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
		cooldownTimerRef.current = setInterval(() => {
			setCooldown((prev) => {
				if (prev <= 1) {
					clearInterval(cooldownTimerRef.current);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	};

	const kickToLogin = (msg) => {
		localStorage.removeItem('mfaSessionToken');
		localStorage.removeItem('mfaMaskedEmail');
		localStorage.removeItem('mfaExpiryMinutes');
		if (msg) {
			notification.warning({ message: 'Please sign in again', description: msg });
		}
		navigate('/login', { replace: true });
	};

	const handleResend = async () => {
		if (!mfaSessionToken || cooldown > 0 || resending) return;
		setResending(true);
		const result = await resend({ mfaSessionToken });
		setResending(false);
		if (result?.status === 1) {
			notification.success({
				message: 'A new sign-in link has been sent to your email.',
				description: 'Always use the link from the most recent email - older ones no longer work.',
			});
			startCooldown(30);
			return;
		}

		const code = result?.data?.code;
		if (code === 'MFA_RESEND_COOLDOWN') {
			const retry = result?.data?.retryAfterSeconds || 30;
			notification.warning({
				message: 'Please wait',
				description: `You can request another link in ${retry}s.`,
			});
			startCooldown(retry);
		} else if (code === 'MFA_MAX_RESENDS') {
			kickToLogin("You've reached the resend limit for this attempt. Please start a fresh sign-in.");
		} else if (code === 'MFA_SESSION_INVALID' || code === 'MFA_LINK_EXPIRED') {
			kickToLogin('This sign-in session has expired. Please start a fresh sign-in.');
		} else {
			notification.error({
				message: 'Could not resend the link',
				description: 'Please try again in a moment, or go back and sign in again.',
			});
		}
	};

	const handleRefresh = () => {
		window.location.reload();
	};

	return (
		<LoginWrapper>
			<Row gutter={[60, 16]}>
				<Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 12, order: 1 }} lg={{ span: 10, order: 1 }} xl={{ span: 10, order: 1 }}>
					<div className='loginLogo'>
						<img src='/images/crowdvox-logo-purple.svg' alt='CrowdVox' />
					</div>
					<div className='loginContainer'>
						<div style={{ textAlign: 'center', padding: '8px 0' }}>
							<div
								style={{
									width: 64,
									height: 64,
									borderRadius: '50%',
									background: 'rgba(46, 69, 218, 0.08)',
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
									marginBottom: 20,
								}}
							>
								<MailOutlined style={{ fontSize: 32, color: '#2E45DA' }} />
							</div>
							<h1 style={{ margin: '0 0 10px 0' }}>Check your email</h1>
							<p style={{ color: '#485066', fontSize: 13, lineHeight: 1.6, margin: '0 0 16px 0' }}>
								We sent a sign-in link to{' '}
								<strong>{maskedEmail || 'your email'}</strong>.
								<br />
								Click the link to sign in.
							</p>

							<div
								style={{
									background: '#F7F5F0',
									border: '1px solid #E2E8EE',
									borderRadius: 8,
									padding: '12px 14px',
									color: '#6D6A67',
									fontSize: 12,
									lineHeight: 1.6,
									textAlign: 'left',
									margin: '0 0 20px 0',
								}}
							>
								<strong style={{ color: '#2C2926' }}>Important:</strong> Open the link
								in <strong>this same browser</strong>. After you click the link and see a
								success page, come back here and click <strong>Refresh</strong> - you'll
								be signed in automatically. If you hit <strong>Resend link</strong>,
								older emails stop working - always use the most recent one.
							</div>

							<ThemeButton
								size='large'
								className='solidPurple'
								icon={<ReloadOutlined />}
								onClick={handleRefresh}
								style={{ minWidth: 200, marginBottom: 16 }}
							>
								Refresh
							</ThemeButton>

							<div style={{ marginTop: 4 }}>
								<span style={{ color: '#6D6A67', fontSize: 13 }}>
									Didn&rsquo;t receive the email?{' '}
								</span>
								<a
									onClick={handleResend}
									style={{
										color: cooldown > 0 || resending ? '#aaa' : '#2E45DA',
										cursor: cooldown > 0 || resending ? 'not-allowed' : 'pointer',
										fontWeight: 600,
										fontSize: 13,
									}}
								>
									{resending
										? 'Sending...'
										: cooldown > 0
											? `Resend in ${cooldown}s`
											: 'Resend link'}
								</a>
							</div>

							<div style={{ marginTop: 12 }}>
								<a
									onClick={() => {
										localStorage.removeItem('mfaSessionToken');
										localStorage.removeItem('mfaMaskedEmail');
										localStorage.removeItem('mfaExpiryMinutes');
										navigate('/login', { replace: true });
									}}
									style={{ color: '#6D6A67', fontSize: 12, cursor: 'pointer' }}
								>
									Back to sign in
								</a>
							</div>
						</div>
					</div>
				</Col>
				<Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 12, order: 1 }} lg={{ span: 14, order: 1 }} xl={{ span: 14, order: 1 }}>
					<div className='loginBannerCircle'>
						<div className='circle'>
							{lottieFailed || !loginLottie ? (
								<img src='/images/login-circle.png' alt='' />
							) : (
								<Lottie
									animationData={loginLottie}
									loop
									autoplay
									style={{ width: '100%', height: '100%' }}
									onError={() => setLottieFailed(true)}
								/>
							)}
						</div>
					</div>
				</Col>
			</Row>
		</LoginWrapper>
	);
};

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) => ({
	resend: (params) => dispatch(resendMagicLinkApi(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SignInLinkCheck);
